/**
 * Where to put the probes, and how sharp each one has to be.
 *
 * Two decisions per probe, and both follow from one number: the distance to
 * the nearest *other* partial the tuning can put on the spectrum.
 *
 *  - **Bandwidth.** Wide is fast, narrow is selective. A probe only has to
 *    reject what is actually nearby, so the low E in standard tuning — whose
 *    nearest company is A2, 27.6 Hz away — can be four times wider, and
 *    therefore four times faster, than a string wedged between neighbours.
 *  - **Window.** Where two partials land on the same frequency they beat
 *    against each other, and only averaging over a beat period settles the
 *    reading. The window is scaled to that.
 *
 * Partials that coincide *exactly* — E2's fourth is E4, and in DADGAD D3's
 * second is D4 — are not competitors to filter out. They are the ghost
 * problem, which no filter solves and `harmonic-model.ts` does.
 */

import { CENTS_PER_OCTAVE } from '../music/pitch'

/** One probe in the plan. */
export interface PlannedProbe {
  /** Frequency to centre on, in Hz. */
  readonly frequency: number
  /** Index of the string this probe belongs to. */
  readonly stringIndex: number
  /** Which partial of that string: 1 is the fundamental. */
  readonly partial: number
  /** Half-bandwidth of the low-pass, in Hz. */
  readonly bandwidthHz: number
  /** Averaging window, in samples. */
  readonly windowSamples: number
  /**
   * True when no string's fundamental sits on this partial.
   *
   * Free partials are the evidence the harmonic model is fitted from: their
   * amplitude belongs to this string alone.
   */
  readonly isFree: boolean
}

/** Everything needed to build a probe bank. */
export interface ProbePlan {
  readonly sampleRate: number
  readonly probes: readonly PlannedProbe[]
  /** Target frequency per string, in Hz. */
  readonly targets: readonly number[]
}

/** Tunable limits of the plan. */
export interface ProbePlanOptions {
  /** Highest partial to probe; beyond this a guitar has little left. */
  readonly maxPartial?: number
  /** Ignore partials above this frequency, in Hz. */
  readonly maxFrequency?: number
  /** Share of the distance to the nearest competitor used as bandwidth. */
  readonly bandwidthShare?: number
  /** Bandwidth floor and ceiling, in Hz. */
  readonly minBandwidthHz?: number
  readonly maxBandwidthHz?: number
  /** Averaging window bounds, in milliseconds. */
  readonly minWindowMs?: number
  readonly maxWindowMs?: number
  /** Beat periods to average over when two partials nearly coincide. */
  readonly beatPeriods?: number
}

export const DEFAULT_PLAN_OPTIONS: Required<ProbePlanOptions> = {
  maxPartial: 6,
  maxFrequency: 2000,
  // Share of the room a probe has before it starts hearing its neighbour.
  // Measured across all nine tunings: 0.35 costs 60 ms per string and buys
  // 0.7 cents, 0.55 saves another 40 ms and costs 1.3. This is the knee.
  bandwidthShare: 0.45,
  minBandwidthHz: 2,
  maxBandwidthHz: 20,
  minWindowMs: 40,
  maxWindowMs: 400,
  // One and a half beat periods, not more. Longer windows do settle a
  // collision further, but the window is also dead time before the first
  // reading, and measured against a full strum the extra settling was not
  // worth the 80 ms it cost on every string.
  beatPeriods: 1.5,
}

/**
 * How close two frequencies must be to count as the same one.
 *
 * Well inside any tuning error: nothing a guitar does moves a partial by four
 * cents relative to another string's fundamental unless they are genuinely
 * different notes.
 */
export const COINCIDENT_CENTS = 4

const centsBetween = (a: number, b: number): number => Math.abs(CENTS_PER_OCTAVE * Math.log2(a / b))

/** Every partial the tuning can put on the spectrum, in Hz. */
function allPartials(targets: readonly number[], maxPartial: number, maxFrequency: number) {
  const partials: number[] = []
  for (const target of targets) {
    for (let n = 1; n <= maxPartial; n += 1) {
      const frequency = target * n
      if (frequency <= maxFrequency) partials.push(frequency)
    }
  }
  return partials
}

/**
 * Distance to the nearest partial that is not this one, in Hz.
 *
 * @returns Infinity when nothing else is in range at all.
 */
export function nearestCompetitorHz(frequency: number, partials: readonly number[]): number {
  let nearest = Number.POSITIVE_INFINITY
  for (const other of partials) {
    if (centsBetween(frequency, other) <= COINCIDENT_CENTS) continue
    nearest = Math.min(nearest, Math.abs(other - frequency))
  }
  return nearest
}

/** Plan the probe bank for a set of string targets. */
export function planProbes(
  targets: readonly number[],
  sampleRate: number,
  options: ProbePlanOptions = {},
): ProbePlan {
  if (!(sampleRate > 0)) throw new RangeError('sampleRate must be positive')
  if (targets.length === 0) return { sampleRate, probes: [], targets: [] }

  const settings = { ...DEFAULT_PLAN_OPTIONS, ...options }
  const partials = allPartials(targets, settings.maxPartial, settings.maxFrequency)
  const probes: PlannedProbe[] = []

  targets.forEach((target, stringIndex) => {
    for (let partial = 1; partial <= settings.maxPartial; partial += 1) {
      const frequency = target * partial
      if (frequency > settings.maxFrequency) break

      const nearest = nearestCompetitorHz(frequency, partials)
      const bandwidthHz = Number.isFinite(nearest)
        ? Math.min(
            settings.maxBandwidthHz,
            Math.max(settings.minBandwidthHz, settings.bandwidthShare * nearest),
          )
        : settings.maxBandwidthHz

      // Anything closer than the window can resolve beats at that rate; the
      // window has to be long enough to average the beat away.
      const beatMs = Number.isFinite(nearest) ? (settings.beatPeriods / nearest) * 1000 : 0
      const windowMs = Math.min(settings.maxWindowMs, Math.max(settings.minWindowMs, beatMs))

      probes.push({
        frequency,
        stringIndex,
        partial,
        bandwidthHz,
        windowSamples: Math.max(2, Math.round((windowMs / 1000) * sampleRate)),
        isFree: !targets.some((other) => centsBetween(other, frequency) <= COINCIDENT_CENTS),
      })
    }
  })

  return { sampleRate, probes, targets: [...targets] }
}

/**
 * The capture range of a string's fundamental probe, in cents.
 *
 * Outside it the bar goes quiet rather than following the string: the probe is
 * deaf to whatever it cannot pass. Worth knowing, because it is the one thing
 * this design gives up against a transform, which sees the whole spectrum.
 */
export function captureRangeCents(plan: ProbePlan, stringIndex: number): number {
  const probe = plan.probes.find((p) => p.stringIndex === stringIndex && p.partial === 1)
  if (!probe) return 0
  return CENTS_PER_OCTAVE * Math.log2((probe.frequency + probe.bandwidthHz) / probe.frequency)
}
